import React, { MouseEventHandler } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { Box, SxProps, TableCell, Theme } from '@mui/material';
import { Cell, flexRender } from '@tanstack/react-table';
import { AvalorTableIds, isNumberType } from '../Table';
import { DataRecord } from './table.types';

interface AvTableV2RowCellProps {
  cell: Cell<DataRecord, unknown>;
  isSelectAllHeader: boolean;
  onClick?: MouseEventHandler<HTMLTableCellElement>;
  sx?: SxProps<Theme>;
}

// Invalidation predicate: return true to re-render, false to allow memoization.
export type AvTableV2ShouldRender = (prev: AvTableV2RowCellProps, next: AvTableV2RowCellProps) => boolean;

const defaultShouldRender: AvTableV2ShouldRender = (prev, next) => {
  if (prev.cell.getValue() !== next.cell.getValue()) return true;
  if (prev.cell.column.id !== next.cell.column.id) return true;
  if (prev.isSelectAllHeader !== next.isSelectAllHeader) return true;
  if (next.isSelectAllHeader) return true;
  return false;
};
const AvTableV2RowCell = ({ cell, isSelectAllHeader, onClick, sx }: AvTableV2RowCellProps) => {
  const columnType = cell.column.columnDef.meta?.type;

  const content = flexRender(cell.column.columnDef.cell, cell.getContext());

  return (
    <TableCell
      padding={isSelectAllHeader ? 'checkbox' : undefined}
      align={isNumberType(columnType) ? 'right' : 'left'}
      className={cell.column.columnDef.meta?.isColumnHighlighted ? 'active-column' : ''}
      onClick={onClick}
      sx={sx}>
      {isSelectAllHeader || cell.column.id === AvalorTableIds.InlineActionsId ? (
        content
      ) : (
        <Box sx={{ width: '100%', overflow: 'hidden', paddingRight: isNumberType(columnType) ? 1 : 0 }}>
          {cell.column.columnDef.meta?.isCustomCell ? content : <AvTooltip>{content}</AvTooltip>}
        </Box>
      )}
    </TableCell>
  );
};

const MemoizedAvTableV2RowCell = React.memo(AvTableV2RowCell, (prev, next) => {
  const columnShouldRender = (next.cell.column.columnDef.meta as any)?.shouldRender as AvTableV2ShouldRender | undefined;
  /**
   * React.memo comparator:
   * return TRUE => props are equal => skip render
   * return FALSE => props differ => re-render
   *
   * Note: our shouldRender predicates return TRUE to re-render,
   * so their result is inverted when returned from this function.
   */
  // must pass default first; then allow column to add more invalidation rules
  if (defaultShouldRender(prev, next)) return false; // re-render
  if (!columnShouldRender || !columnShouldRender(prev, next)) return true; // skip
  return false; // re-render
});

export default MemoizedAvTableV2RowCell;
